import React from "react";
import { icons } from "../../../svg";
import "./QuotedRateRequestPanel.css";

type QuotedRateRequestPanelProps = {
  firstShipDate: string;
  carsSold: string;
  comments: string;
  canSubmit: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onFirstShipDateChange: (value: string) => void;
  onCarsSoldChange: (value: string) => void;
  onCommentsChange: (value: string) => void;
};

const QuotedRateRequestPanel: React.FC<QuotedRateRequestPanelProps> = ({
  firstShipDate,
  carsSold,
  comments,
  canSubmit,
  isSubmitting,
  onClose,
  onSubmit,
  onFirstShipDateChange,
  onCarsSoldChange,
  onCommentsChange,
}) => {
  const isValidFirstShipDate = (value: string): boolean => {
    const trimmedValue = value.trim();
    const dateMatch = trimmedValue.match(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/,
    );

    if (!dateMatch) {
      return false;
    }

    const month = Number(dateMatch[1]);
    const day = Number(dateMatch[2]);
    const year = Number(dateMatch[3]);
    const parsedDate = new Date(year, month - 1, day);

    return (
      parsedDate.getFullYear() === year &&
      parsedDate.getMonth() === month - 1 &&
      parsedDate.getDate() === day
    );
  };

  const isValidCarsSoldInput = (value: string): boolean => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    return /^\d+(\.\d+)?$/.test(trimmedValue);
  };

  const showFirstShipDateWarning =
    firstShipDate.trim().length > 0 && !isValidFirstShipDate(firstShipDate);
  const showCarsSoldWarning =
    carsSold.trim().length > 0 && !isValidCarsSoldInput(carsSold);

  const formatFirstShipDateInput = (
    value: string,
    finalizeSingleDigits: boolean,
    includeTrailingSeparators: boolean,
  ): string => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const [firstDigit] = digits;
    if (!digits) {
      return "";
    }

    let index = 0;
    let month = "";
    let monthComplete = false;

    if (firstDigit !== "0" && firstDigit !== "1") {
      month = `0${firstDigit}`;
      index = 1;
      monthComplete = true;
    } else if (digits.length >= 2) {
      month = `${firstDigit}${digits[1]}`;
      index = 2;
      monthComplete = true;
    } else {
      month = firstDigit;
      if (finalizeSingleDigits) {
        month = `0${firstDigit}`;
        monthComplete = true;
        index = 1;
      }
    }

    if (!monthComplete) {
      return month;
    }

    const remaining = digits.slice(index);
    const dayDigits = remaining.slice(0, 2);
    const year = remaining.slice(2, 6);

    let day = dayDigits;
    if (finalizeSingleDigits && dayDigits.length === 1) {
      day = `0${dayDigits}`;
    }

    if (!day) {
      return includeTrailingSeparators ? `${month}/` : month;
    }

    if (day.length < 2) {
      return `${month}/${day}`;
    }

    if (!year) {
      return includeTrailingSeparators ? `${month}/${day}/` : `${month}/${day}`;
    }

    return `${month}/${day}/${year}`;
  };

  return (
    <div className="quoted-rate-request-panel-overlay">
      <div className="quoted-rate-request-panel">
        <div className="quoted-rate-request-header">
          <div>
            <h3 className="quoted-rate-request-title">Quoted Rail Rate</h3>
            <div className="quoted-rate-request-subtitle">
              Please fill out this form to notify the rail team to publish the
              rate.
            </div>
          </div>
          <button
            type="button"
            className="quoted-rate-request-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            <img
              src={icons.CloseBlackWhiteIcon}
              alt="Close"
              className="quoted-rate-request-close-icon"
            />
          </button>
        </div>

        <div className="quoted-rate-request-body">
          <div className="quoted-rate-request-field">
            <div className="quoted-rate-request-label">
              First Ship Date
              <span className="quoted-rate-request-required">*</span>
            </div>
            <div className="quoted-rate-request-helper">
              The date the first rail car is expected to ship
            </div>
            <input
              id="first-ship-date-input"
              className="quoted-rate-request-input"
              placeholder="e.g., 06/15/2026"
              value={firstShipDate}
              onChange={(event) =>
                onFirstShipDateChange(
                  (() => {
                    const inputType = (event.nativeEvent as InputEvent)
                      ?.inputType;
                    const isDeleting = !!inputType?.startsWith("delete");

                    return formatFirstShipDateInput(
                      event.target.value,
                      false,
                      !isDeleting,
                    );
                  })(),
                )
              }
              onBlur={(event) =>
                onFirstShipDateChange(
                  formatFirstShipDateInput(event.target.value, true, false),
                )
              }
              maxLength={10}
            />
            {showFirstShipDateWarning && (
              <div className="quoted-rate-request-warning">
                Please enter a valid date in mm/dd/yyyy format
              </div>
            )}
          </div>

          <div className="quoted-rate-request-field">
            <div className="quoted-rate-request-label">
              Cars Sold
              <span className="quoted-rate-request-required">*</span>
            </div>
            <div className="quoted-rate-request-helper">
              The total number of rail cars sold
            </div>
            <input
              id="cars-sold-input"
              className="quoted-rate-request-input"
              placeholder="e.g., 100"
              value={carsSold}
              onChange={(event) => onCarsSoldChange(event.target.value)}
            />
            {showCarsSoldWarning && (
              <div className="quoted-rate-request-warning">
                Please enter a valid number
              </div>
            )}
          </div>

          <div className="quoted-rate-request-field">
            <div className="quoted-rate-request-label">Comments</div>
            <textarea
              id="quoted-rate-comments-input"
              className="quoted-rate-request-textarea"
              value={comments}
              onChange={(event) => onCommentsChange(event.target.value)}
            />
          </div>
        </div>

        <div className="quoted-rate-request-footer">
          <button
            type="button"
            className="quoted-rate-request-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="quoted-rate-request-submit-button"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotedRateRequestPanel;
